import { errorIsStatus, useClientPortalAPI, useQueryParams } from "@certn/api";
import { rem } from "@certn/cascadia";
import {
  AvatarWithName,
  Card,
  ColumnDefinition,
  DataTable,
  Illustration,
  LinkButton,
  Pagination,
  SectionMessage,
  Text,
} from "@certn/cascadia-react";
import { formatDate, formatTime, parseName } from "@certn/utils";
import { css } from "@emotion/css";
import { PreviewRubricModal } from "../../../components/preview-adjudication-rubric/PreviewRubricModal";
import { components } from "@certn/api/generated-schemas/client-portal";
import { useTranslation } from "react-i18next";

interface AdjudicationActivityProps {
  adjudicationId?: string;
}

function ActivityLogEmptyState() {
  const { t } = useTranslation();
  return (
    <>
      <Illustration name="no-results" />
      <Text style="global/body-secondary" align="center" color="text-weak">
        {t("No activity logs found.")}
      </Text>
    </>
  );
}

export function AdjudicationActivity({
  adjudicationId,
}: AdjudicationActivityProps) {
  const { t } = useTranslation();
  const { params, setParam } = useQueryParams();

  const page = params.page ? Number(params.page) : 1;
  const pageSize = params.page_size ? Number(params.page_size) : 25;

  const {
    data: activityLogs,
    error,
    isLoading,
  } = useClientPortalAPI(
    `/api/internal/client-portal/adjudication-rubrics/${adjudicationId}/activity-logs/`,
    {
      page,
      page_size: pageSize,
    }
  );

  const rows =
    activityLogs?.results?.map(
      (
        activity: components["schemas"]["AdjudicationRubricActivityLog"],
        index: number
      ) => {
        return { ...activity, id: index.toString() };
      }
    ) ?? [];

  const columns: ColumnDefinition<(typeof rows)[number]>[] = [
    {
      key: "timestamp",
      name: t("Date"),
      width: "15%",
      isRowHeader: true,
      render(item) {
        return (
          <Text style="form/body-secondary">{formatDate(item.timestamp)}</Text>
        );
      },
    },
    {
      key: "time",
      name: t("Time"),
      width: "15%",
      isRowHeader: true,
      render(item) {
        return (
          <Text style="form/body-secondary">{formatTime(item.timestamp)}</Text>
        );
      },
    },
    {
      key: "activity_description",
      name: t("Activity description"),
      width: "30%",
      isRowHeader: true,
      render(item) {
        return (
          <Text style="form/body-secondary">{item.activity_description}</Text>
        );
      },
    },
    {
      key: "preview",
      name: t("Preview"),
      width: "15%",
      isRowHeader: true,
      render(item) {
        return (
          <>
            {item.check_ruleset_version ? (
              <PreviewRubricModal
                triggerComponent={
                  <LinkButton
                    testID={"preview-adjudication-rubric-button-activity-log"}
                    textStyle="global/body-secondary-link"
                    textColor="text-base"
                  >
                    {t("Preview link")}
                  </LinkButton>
                }
                rulesets={[
                  {
                    check_type: item.check_ruleset_version.check_type,
                    criteria: item.check_ruleset_version.criteria,
                    id: item.check_ruleset_version.id,
                  },
                ]}
              />
            ) : null}
          </>
        );
      },
    },
    {
      key: "actor",
      name: t("By"),
      width: "25%",
      isRowHeader: true,
      render(item) {
        return <AvatarWithName name={parseName(item.actor.auth0_full_name)} />;
      },
    },
  ];

  return (
    <Card padding="space-5" type="large" responsive>
      <div className={styles.container}>
        {error && !errorIsStatus(error, 404) && (
          <SectionMessage
            type="danger"
            icon="error"
            sectionTitle={t("Unable to retrieve activity logs")}
          >
            {String(error.message)}
          </SectionMessage>
        )}
        <Text style="global/heading-large">{t("Activity log")}</Text>
        <DataTable
          isLoading={isLoading}
          testId="adjudication-activity-log-data-table"
          columns={columns}
          rows={rows}
          aria-label="adjudication-activity-log-table"
          renderEmptyState={() => <ActivityLogEmptyState />}
        />
        <Pagination
          setPage={(page) => setParam("page", page.toString())}
          setPageCount={(pageCount) =>
            setParam("page_size", pageCount.toString(), true)
          }
          total={activityLogs?.count || 0}
          page={page}
          pageCount={pageSize}
          isLoading={isLoading}
        />
      </div>
    </Card>
  );
}

const styles = {
  container: css({
    display: "flex",
    flexDirection: "column",
    gap: rem("space-6"),
  }),
};
